import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
	faGraduationCap,
	faChevronDown,
	faChevronUp,
	faPlus,
} from "@fortawesome/free-solid-svg-icons";

import { PageContainer } from "../../components/layout";
import { NotificationContainer } from "../../components/common";
import { AssessmentGradesModal } from "../../components/modals";
import { useTranslation } from "../../hooks";
import {
	useAssessmentsData,
	useAssessmentForm,
	useAssessmentModal,
} from "./hooks";
import {
	AssessmentsSidebar,
	MonthlyAssessments,
	AssessmentsList,
} from "./components";
import { ASSESSMENT_TYPES } from "./hooks/useAssessmentForm";
import styles from "./AssessmentsPage.module.css";

const AssessmentsPage = () => {
	const { t } = useTranslation();

	const {
		user,
		departmentId,
		currentDepartment,
		departmentSubjects,
		groupedAssessments,
		currentMonthAssessments,
		totalMonthPoints,
		monthsToDisplay,
		selectedSemester,
		setSelectedSemester,
		selectedSubject,
		setSelectedSubject,
		selectedMonth,
		setSelectedMonth,
		isMobile,
		assessmentsLoading,
		refetchAssessments,
		handleDeleteAssessment,
	} = useAssessmentsData();

	const formProps = useAssessmentForm({
		selectedSemester,
		selectedMonth,
		selectedSubject,
		departmentId,
		user,
		refetchAssessments,
		isMobile,
		currentMonthAssessments,
	});

	// Extract notifications from formProps
	const { notifications, removeNotification } = formProps;

	const {
		selectedAssessment,
		isGradesModalOpen,
		openGradesModal,
		closeGradesModal,
	} = useAssessmentModal();

	return (
		<PageContainer className={styles.assessmentsPage}>
			{/* Only addition: NotificationContainer */}
			<NotificationContainer
				notifications={notifications}
				onRemoveNotification={removeNotification}
			/>

			<div
				className={`${styles.assessmentsContainer} ${
					isMobile ? styles.mobileView : ""
				}`}
			>
				{/* Header - hidden on mobile */}
				<header className={styles.header}>
					<div className={styles.title}>
						<h1>
							<FontAwesomeIcon
								icon={faGraduationCap}
								className={styles.titleIcon}
							/>
							{t("assessment.assessmentManagement") || "Assessment Management"}
							{currentDepartment && (
								<span className={styles.departmentName}>
									{" "}
									for {currentDepartment.departmentName}th grade
								</span>
							)}
						</h1>
					</div>
				</header>

				{/* Mobile: Form toggle button */}
				{isMobile && (
					<div className={styles.mobileControls}>
						<button
							className={`${styles.btn} ${styles.btnSecondary} ${styles.btnBlock}`}
							onClick={() => formProps.setFormVisible(!formProps.formVisible)}
						>
							<FontAwesomeIcon
								icon={formProps.formVisible ? faChevronUp : faChevronDown}
							/>
							{formProps.formVisible
								? t("assessment.hideForm") || "Hide Form"
								: t("assessment.addNewAssessment") || "Add New Assessment"}
						</button>
					</div>
				)}

				{/* Mobile: Form (collapsible) */}
				{isMobile && formProps.formVisible && (
					<div className={styles.mobileForm}>
						<div className={styles.mobileFormContent}>
							{/* Semester switch */}
							<div className={styles.formGroup}>
								<label className={styles.formLabel}>
									{t("assessment.semester") || "Semester"}
								</label>
								<div className={styles.semesterTabs}>
									<div
										className={`${styles.semesterTab} ${
											selectedSemester === "First Semester" ? styles.active : ""
										}`}
										onClick={() => setSelectedSemester("First Semester")}
									>
										{t("assessment.firstSemester") || "First Semester"}
									</div>
									<div
										className={`${styles.semesterTab} ${
											selectedSemester === "Second Semester"
												? styles.active
												: ""
										}`}
										onClick={() => setSelectedSemester("Second Semester")}
									>
										{t("assessment.secondSemester") || "Second Semester"}
									</div>
								</div>
							</div>

							{/* Subject selector */}
							<div className={styles.formGroup}>
								<label className={styles.formLabel}>
									{t("assessment.subject") || "Subject"}
								</label>
								<select
									className={styles.formControl}
									value={selectedSubject || ""}
									onChange={(e) => setSelectedSubject(e.target.value)}
									disabled={departmentSubjects.length === 0}
								>
									<option value="" disabled>
										{t("assessment.selectSubject") || "Select a subject"}
									</option>
									{departmentSubjects.map((subject) => (
										<option key={subject.id} value={subject.id}>
											{subject.name}
										</option>
									))}
								</select>
							</div>

							<h2 className={styles.mobileFormTitle}>
								{t("assessment.addNewAssessment") || "Add New Assessment"}
							</h2>

							{/* Assessment form fields */}
							<div className={styles.formGroup}>
								<label className={styles.formLabel} htmlFor="mobile-title">
									{t("assessment.assessmentTitle") || "Assessment Title"}
								</label>
								<input
									id="mobile-title"
									type="text"
									className={styles.formControl}
									placeholder={
										t("assessment.enterTitle") || "Enter assessment title"
									}
									value={formProps.title}
									onChange={(e) => formProps.setTitle(e.target.value)}
								/>
							</div>

							<div className={styles.formGroup}>
								<label className={styles.formLabel} htmlFor="mobile-type">
									{t("assessment.assessmentType") || "Assessment Type"}
								</label>
								<select
									id="mobile-type"
									className={styles.formControl}
									value={formProps.type}
									onChange={(e) => formProps.setType(e.target.value)}
								>
									{ASSESSMENT_TYPES.map((assessmentType) => (
										<option key={assessmentType} value={assessmentType}>
											{assessmentType}
										</option>
									))}
								</select>
							</div>

							<div className={styles.formRow}>
								<div className={styles.formGroup}>
									<label className={styles.formLabel} htmlFor="mobile-points">
										{t("assessment.points") || "Points"}
									</label>
									<input
										id="mobile-points"
										type="number"
										className={styles.formControl}
										placeholder={t("assessment.points") || "Points"}
										min="0.1"
										step="0.1"
										max="100"
										value={formProps.points}
										onChange={(e) => formProps.setPoints(e.target.value)}
									/>
								</div>

								<div className={styles.formGroup}>
									<label className={styles.formLabel} htmlFor="mobile-month">
										{t("assessment.month") || "Month"}
									</label>
									<select
										id="mobile-month"
										className={styles.formControl}
										value={formProps.formMonth}
										onChange={(e) => formProps.setFormMonth(e.target.value)}
									>
										{formProps.monthsToDisplay.map((month) => (
											<option key={month} value={month}>
												{month}
											</option>
										))}
									</select>
								</div>
							</div>

							<button
								className={`${styles.btn} ${styles.btnPrimary} ${styles.btnBlock}`}
								onClick={formProps.handleAddAssessment}
								disabled={!selectedSubject || formProps.isCreating}
							>
								<FontAwesomeIcon icon={faPlus} />
								{formProps.isCreating
									? "Adding..."
									: t("assessment.addAssessment") || "Add Assessment"}
							</button>
						</div>
					</div>
				)}

				{/* Main layout */}
				<div
					className={`${styles.layout} ${
						assessmentsLoading ? styles.assessmentsLoading : ""
					}`}
				>
					{/* Desktop: Sidebar with controls */}
					{!isMobile && (
						<AssessmentsSidebar
							selectedSemester={selectedSemester}
							setSelectedSemester={setSelectedSemester}
							departmentSubjects={departmentSubjects}
							selectedSubject={selectedSubject}
							setSelectedSubject={setSelectedSubject}
							isMobile={isMobile}
							{...formProps}
						/>
					)}

					{/* Main content area */}
					<main className={styles.main}>
						{/* Month navigation */}
						<MonthlyAssessments
							monthsToDisplay={monthsToDisplay}
							groupedAssessments={groupedAssessments}
							selectedMonth={selectedMonth}
							setSelectedMonth={setSelectedMonth}
						/>

						{/* Assessment list */}
						<div className={styles.assessmentList}>
							<AssessmentsList
								assessmentsLoading={assessmentsLoading}
								currentMonthAssessments={currentMonthAssessments}
								totalMonthPoints={totalMonthPoints}
								selectedMonth={selectedMonth}
								onAssessmentClick={openGradesModal}
								onDeleteAssessment={handleDeleteAssessment}
							/>
						</div>
					</main>
				</div>
			</div>

			{isGradesModalOpen && selectedAssessment && (
				<AssessmentGradesModal
					assessment={selectedAssessment}
					departmentId={departmentId}
					onClose={closeGradesModal}
				/>
			)}
		</PageContainer>
	);
};

export default AssessmentsPage;
